/** The one place an operator verb becomes an api call. */

import type {
  BindingTargetKind,
  MountRecord,
  ProgressReset,
  SpaceLoadResult,
  SpaceRecord,
  StopAllResult,
} from "@nebulize/sdk";

import { api } from "@/lib/api";
import { spaces } from "@/lib/spaces";

import type { CascadeVerb } from "../../lib/cascade";
import type { ActivityVerb } from "../../lib/runState";
import type { ActivityFanOutOutcome, AddedActivityRow } from "../../types";

type MountVerb = Exclude<ActivityVerb, "Start" | "Reset progress">;

const ON_MOUNT: Record<MountVerb, (mountId: string) => Promise<MountRecord>> = {
  Stop: spaces.unmount,
  Restart: spaces.restart,
  Pause: spaces.pause,
  Resume: spaces.resume,
};

/** Start adds nothing: item 2.3 already added this row, and this mounts it. */
export async function actOnActivity(row: AddedActivityRow, verb: ActivityVerb): Promise<void> {
  if (verb === "Start") {
    await spaces.mount(row.space_id, { added_activity_id: row.added_activity_id });
    return;
  }
  if (verb === "Reset progress") {
    await api.post<ProgressReset>(`/v1/added-activities/${row.added_activity_id}/reset`);
    return;
  }
  const mountId = row.mount?.mount_id;
  if (mountId === undefined) throw new Error("not running");
  await ON_MOUNT[verb](mountId);
}

function pausedOutcome(
  mount: MountRecord,
  nameOf: (addedActivityId: string) => string,
): ActivityFanOutOutcome {
  const addedActivityId = mount.added_activity_id ?? "";
  return {
    added_activity_id: addedActivityId,
    name: nameOf(addedActivityId),
    succeeded: true,
    mount,
  };
}

/** One outcome per activity, refusals included. Nothing here collapses them. */
export async function actOnSpace(
  spaceId: string,
  verb: CascadeVerb,
  nameOf: (addedActivityId: string) => string,
): Promise<ActivityFanOutOutcome[]> {
  if (verb === "Start") return (await spaces.startAll(spaceId)).activities ?? [];
  if (verb === "Stop") return (await spaces.stopAll(spaceId)).activities ?? [];
  const result = await spaces.pauseSpace(spaceId);
  return (result.paused ?? []).map((mount) => pausedOutcome(mount, nameOf));
}

/** Loading decides which space every stable address resolves through. It is not Start or Stop. */
export function loadSpace(spaceId: string): Promise<SpaceLoadResult> {
  return api.post<SpaceLoadResult>(`/v1/spaces/${spaceId}/load`, {
    start: false,
    stop_previous: false,
  });
}

/** 204 when this space was not the loaded one, which is an answer rather than a failure. */
export function unloadSpace(spaceId: string): Promise<StopAllResult | undefined> {
  return api.post<StopAllResult | undefined>(`/v1/spaces/${spaceId}/unload`, { stop: false });
}

export function configureSettings(
  addedActivityId: string,
  settings: Record<string, unknown>,
): Promise<unknown> {
  return api.post(`/v1/added-activities/${addedActivityId}/settings`, { settings });
}

export function renameAddedActivity(addedActivityId: string, name: string): Promise<unknown> {
  return api.post(`/v1/added-activities/${addedActivityId}/rename`, { name });
}

/** Repin this record to another published version of the same activity. */
export function moveAddedActivityVersion(
  addedActivityId: string,
  activityVersion: string,
  dropUnsupported: boolean,
): Promise<unknown> {
  return api.post(`/v1/added-activities/${addedActivityId}/version`, {
    activity_version: activityVersion,
    drop_unsupported: dropUnsupported,
  });
}

export function removeAddedActivity(addedActivityId: string): Promise<undefined> {
  return api.delete<undefined>(`/v1/added-activities/${addedActivityId}`);
}

export interface BindBody {
  requirement_name: string;
  position: number;
  target_kind: BindingTargetKind;
  target_id: string;
}

/** SES-013 item 3.3. `position` is always explicit and never inferred here. */
export function bindPosition(addedActivityId: string, body: BindBody): Promise<unknown> {
  return api.post(`/v1/added-activities/${addedActivityId}/bindings`, body);
}

export function unbindPosition(bindingId: string): Promise<undefined> {
  return api.delete<undefined>(`/v1/bindings/${bindingId}`);
}

export function renameSpace(spaceId: string, name: string): Promise<SpaceRecord> {
  return api.post<SpaceRecord>(`/v1/spaces/${spaceId}/rename`, { name });
}

export function archiveSpace(spaceId: string): Promise<SpaceRecord> {
  return api.post<SpaceRecord>(`/v1/spaces/${spaceId}/archive`);
}

/** The inverse of archive, and the same authority: archiving is reversible until purge. */
export function restoreSpace(spaceId: string): Promise<SpaceRecord> {
  return api.post<SpaceRecord>(`/v1/spaces/${spaceId}/restore`);
}
